import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "domains/commons/contexts/HotkeysProvider";
import Card from "domains/file-manager/components/FileManagerImage/Card";
import FmCard from "domains/file-manager/components/FileManagerV2/FmCard";
import { mapAssetsToImagesFiles } from "domains/file-manager/interfaces";
import { TOP_BAR_HEIGHT } from "domains/navigation/constants/TopBar";
import useRouter from "domains/navigation/hooks/useRouter";
import Icon from "domains/ui/components/Icon";
import { useHover } from "domains/ui/hooks/useHover";
import { motion } from "framer-motion";
import {
  GetAssetsByAssetIdApiResponse,
  GetModelsByModelIdApiResponse,
} from "infra/api/generated/api";

import {
  Box,
  Flex,
  IconButton,
  Text,
  useMergeRefs,
  VStack,
} from "@chakra-ui/react";

type AssetWithModel = GetAssetsByAssetIdApiResponse["asset"] & {
  model: GetModelsByModelIdApiResponse["model"];
};

interface NewUserEmptyStateProps {
  assets: AssetWithModel[];
}

const NewUserEmptyState = ({ assets }: NewUserEmptyStateProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [hoverRef, isHovered] = useHover();

  useEffect(() => {
    if (!isAutoPlay || isHovered || !assets.length) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % assets.length);
    }, 5_000);
    return () => clearInterval(interval);
  }, [assets.length, isAutoPlay, isHovered, setCurrentIndex]);

  useEffect(() => {
    if (isAutoPlay) return;
    const timeout = setTimeout(() => {
      setIsAutoPlay(true);
    }, 15_000);
    return () => clearTimeout(timeout);
  }, [isAutoPlay]);

  const handlePrevious = useCallback(() => {
    if (!assets.length) return;
    setIsAutoPlay(false);
    setCurrentIndex((prev) => (prev - 1 + assets.length) % assets.length);
  }, [assets.length, setCurrentIndex, setIsAutoPlay]);

  const handleNext = useCallback(() => {
    if (!assets.length) return;
    setIsAutoPlay(false);
    setCurrentIndex((prev) => (prev + 1) % assets.length);
  }, [assets.length, setCurrentIndex, setIsAutoPlay]);
  useHotkeys(
    "left",
    () => {
      handlePrevious();
    },
    { enabled: true }
  );

  useHotkeys(
    "right",
    () => {
      handleNext();
    },
    { enabled: true }
  );

  const currentAsset: AssetWithModel | undefined = assets[currentIndex];

  // Used to get the width of the container
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>();
  const [containerHeight, setContainerHeight] = useState<number>();

  // Find a scale so the carousel fits both container width and height (50% of height)
  const carouselScale = useMemo(() => {
    if (!containerWidth || !containerHeight) return 1;
    const heightScale = containerHeight / 2 / 512; // 50% of container height
    const widthScale = containerWidth / 1_000;
    return Math.min(1, Math.min(heightScale, widthScale));
  }, [containerWidth, containerHeight]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      setContainerWidth(entry.contentRect.width);
      setContainerHeight(entry.contentRect.height);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const mergedContainerRef = useMergeRefs(containerRef, hoverRef);

  const router = useRouter();

  if (!assets?.length) {
    return null;
  }

  return (
    <Flex
      ref={mergedContainerRef}
      align="center"
      justify="center"
      direction="column"
      flex={1}
      w="full"
      maxH={`calc(100% - ${TOP_BAR_HEIGHT}px)`}
      color="textPrimary"
      bg="background.500"
      outline="none"
      id="empty-state-carousel"
      tabIndex={0}
    >
      {/* Content Section */}
      <VStack
        maxW="800px"
        h="120px"
        minH="120px"
        maxH="120px"
        px={4}
        spacing={0}
      >
        <Text color="textPrimary" textAlign="center" size="title.1.5xl">
          {currentAsset?.model?.name}
        </Text>

        <Text
          key={currentAsset?.metadata?.prompt}
          maxW="600px"
          mb={4}
          color="textTertiary"
          lineHeight="20px"
          textAlign="center"
          noOfLines={2}
        >
          {currentAsset?.metadata?.prompt}
        </Text>
      </VStack>

      {/* Carousel Section */}
      <Box
        pos="relative"
        w="1000px"
        h="500px"
        transform={`scale(${carouselScale}) translateZ(0)`}
      >
        {/* Spotlight */}
        <Box
          pos="absolute"
          zIndex={2}
          top="-100px"
          left="0"
          w="450px"
          h="450px"
          bg="rgba(0, 119, 255, 0.20)"
          pointerEvents="none"
          filter="blur(100px)"
        />

        {/* Spotlight */}
        <Box
          pos="absolute"
          zIndex={2}
          top="200px"
          right="0"
          w="450px"
          h="450px"
          bg="rgba(0, 119, 255, 0.20)"
          pointerEvents="none"
          filter="blur(100px)"
        />

        <Flex pos="relative" align="center" justify="center" w="100%" h="100%">
          {/* Previous Arrow */}
          <IconButton
            pos="absolute"
            zIndex={20}
            left="calc(50% - 320px)"
            borderRadius="full"
            transform={`scale(${1 / carouselScale} )`} // Compensate for the scale of the carousel to have correct size buttons
            aria-label="Previous image"
            icon={<Icon id="Ui/ChevronLeft" />}
            onClick={handlePrevious}
            variant="secondary"
          />

          {/* Next Arrow */}
          <IconButton
            pos="absolute"
            zIndex={20}
            right="calc(50% - 320px)"
            borderRadius="full"
            transform={`scale(${1 / carouselScale})`} // Compensate for the scale of the carousel to have correct size buttons
            aria-label="Next image"
            icon={<Icon id="Ui/ChevronRight" />}
            onClick={handleNext}
            variant="secondary"
          />

          {assets.map((asset, index) => {
            const position =
              (index - currentIndex + assets.length) % assets.length;
            const adjustedPosition =
              position > assets.length / 2
                ? position - assets.length
                : position;

            // Only show 5 images at a time (-2, -1, 0, 1, 2)
            if (Math.abs(adjustedPosition) > 2) {
              return null;
            }
            const isInMiddle = position === 0;

            // Calculate scale and blur based on position
            const imageScale =
              position === 0 ? 1 : Math.pow(0.8, Math.abs(adjustedPosition));
            const blur = Math.abs(adjustedPosition) * 4;
            const xOffset = adjustedPosition * 300 * imageScale;
            const zIndex = 10 - Math.abs(adjustedPosition);

            return (
              <motion.div
                key={asset.id}
                initial={{ scale: 0, x: xOffset, y: 0 }}
                animate={{
                  scale: imageScale,
                  x: xOffset,
                  y: 0,
                  filter: `blur(${blur}px) brightness(${
                    1 - Math.abs(adjustedPosition) * 0.2
                  })`,
                  zIndex,
                }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }}
                style={{
                  scale: imageScale,
                  position: "absolute",
                  transformOrigin: "center bottom",
                  cursor: position !== 0 ? "pointer" : "default",
                  x: xOffset,
                  y: 0,
                  filter: `blur(${blur}px) brightness(${
                    1 - Math.abs(adjustedPosition) * 0.2
                  })`,
                  zIndex,
                }}
                onClick={() => {
                  if (position !== 0) {
                    setIsAutoPlay(false);
                    setCurrentIndex(index);
                  }
                }}
              >
                <Box
                  pos="relative"
                  overflow="hidden"
                  w="512px"
                  h="512px"
                  borderWidth={position === 0 ? "13px" : "0px"}
                  borderColor={
                    position === 0 ? "rgba(255, 255, 255, 0.2)" : "transparent"
                  }
                  borderRadius="50px"
                  shadow={
                    position === 0 ? "0 0 30px rgba(255,255,255,0.1)" : "none"
                  }
                >
                  <FmCard
                    type="image"
                    file={mapAssetsToImagesFiles([asset])[0]}
                    isHovered={false}
                    thumbnail={asset.url + "&width=512"}
                    isBlurred={false}
                    imgProps={{
                      alt: asset.description ?? asset.metadata?.prompt ?? "",
                    }}
                    onClick={() => {
                      void router.push({
                        query: {
                          openAssetId: asset.id,
                          ...router.query,
                        },
                      });
                    }}
                  >
                    <Box
                      sx={{
                        // Compensate for the scale of the carousel to have correct size buttons
                        "& div:has( > button)": {
                          transform: `scale(${1 / carouselScale}) translateX(-${
                            (1 - carouselScale) * 50
                          }%)`,
                        },
                      }}
                      py={4}
                    >
                      <Card
                        isVaryEnabled
                        cardWidth={512}
                        file={mapAssetsToImagesFiles([asset])[0]}
                        containerRef={containerRef}
                        isBlurred={false}
                        isHovered={isInMiddle}
                        isPinned={false}
                        variant="image"
                        onClick={() => {
                          void router.push({
                            query: {
                              openAssetId: asset.id,
                              ...router.query,
                            },
                          });
                        }}
                      />
                    </Box>
                  </FmCard>
                </Box>
              </motion.div>
            );
          })}
        </Flex>
        {/* Blur box on the bottom of the to blur what is behind it */}
        <Box
          pos="absolute"
          zIndex={1_000}
          bottom="-200px"
          left="-1000px"
          w="300%"
          h="512px"
          bg="black"
          pointerEvents="none"
          style={{
            mask: "linear-gradient(to bottom, transparent, black 40%)",
            WebkitMask: "linear-gradient(to bottom, transparent, black 40%)",
            background: "rgba(0, 0, 0, 0.01)",
            backdropFilter: "blur(70px)",
            opacity: 0.45,
          }}
        />
      </Box>
    </Flex>
  );
};

export default memo(NewUserEmptyState);
